import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Icon, PlainButton, Stack, Text } from '@customerio/pluma-components/react';
import type { SlashCommand } from './types';
import * as menuStyles from './slash-commands.css';

type SlashCommandMenuProps = {
  items: SlashCommand[];
  command: (item: SlashCommand) => void;
};

export type SlashCommandMenuHandle = {
  onKeyDown: (event: KeyboardEvent) => boolean;
};

export const SlashCommandMenu = forwardRef<SlashCommandMenuHandle, SlashCommandMenuProps>(function SlashCommandMenu(
  { items, command },
  ref,
) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);

  // Reset selection when items change
  useEffect(() => {
    setSelectedIndex(0);
  }, [items]);

  // Scroll the selected item into view
  useEffect(() => {
    const list = listRef.current;
    if (list == null) return;
    // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
    const selected = list.children[selectedIndex] as HTMLElement | undefined;
    selected?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  useImperativeHandle(
    ref,
    () => ({
      onKeyDown: (event: KeyboardEvent) => {
        if (event.key === 'ArrowUp') {
          setSelectedIndex((previous) => (previous <= 0 ? items.length - 1 : previous - 1));
          return true;
        }

        if (event.key === 'ArrowDown') {
          setSelectedIndex((previous) => (previous >= items.length - 1 ? 0 : previous + 1));
          return true;
        }

        if (event.key === 'Enter' || event.key === 'Tab') {
          const item = items[selectedIndex];
          if (item != null) {
            command(item);
          }
          return true;
        }

        return false;
      },
    }),
    [items, selectedIndex, command],
  );

  if (items.length === 0) {
    return null;
  }

  return (
    <div className={menuStyles.menu} ref={listRef} role="listbox">
      {items.map((item, index) => (
        <PlainButton
          key={item.id}
          className={`${menuStyles.menuItem} ${index === selectedIndex ? menuStyles.menuItemActive : ''}`}
          role="option"
          aria-selected={index === selectedIndex}
          onClick={() => command(item)}
          onMouseEnter={() => setSelectedIndex(index)}
        >
          <Icon name={item.icon} size="sm" color="subtle" />
          <Stack gap="0" minW="0">
            <Text size="sm" truncate>
              {item.name}
            </Text>
            <span className={menuStyles.menuItemDescription}>{item.description}</span>
          </Stack>
        </PlainButton>
      ))}
    </div>
  );
});
